import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Legend,
  Area,
  ComposedChart,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { fmtPct, fmtMoney } from '@/lib/format-utils'

const KEY_DAYS = [0, 3, 7, 14, 30, 60, 90, 120, 150]

// Custom label component for ReferenceLine
const CustomLabel = ({ viewBox, value, fill, position }) => {
  const { x, y, width, height } = viewBox || {}

  let textX = x
  let textY = y
  let textAnchor = 'start'

  if (position === 'insideTopRight') {
    textX = x + 5
    textY = 20
    textAnchor = 'start'
  } else if (position === 'insideRight') {
    textX = (width || 0) - 10
    textY = y - 5
    textAnchor = 'end'
  }

  return (
    <text
      x={textX}
      y={textY}
      fill={fill}
      fontSize={12}
      fontWeight="bold"
      textAnchor={textAnchor}
    >
      {value}
    </text>
  )
}

const ModelCurve = ({ roasData, dailyRates }) => {
  if (!roasData || roasData.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
        No recovery model data available for the selected parameters.
      </div>
    )
  }

  // Merge roasData and dailyRates
  const mergedData = roasData.map((item) => {
    const dailyRate = dailyRates.find((d) => d.day === item.day)
    return {
      ...item,
      dailyRate: dailyRate?.dailyRate || 0,
    }
  })

  // Calculate Y axis max for left axis (ROAS)
  const maxRoas = Math.max(...roasData.map((d) => d.roas))
  const yAxisMaxLeft = Math.ceil(maxRoas / 20) * 20

  // Calculate Y axis max for right axis (Daily Rate)
  const maxDailyRate = Math.max(...dailyRates.map((d) => d.dailyRate || 0))
  const yAxisMaxRight = Math.ceil(maxDailyRate / 2) * 2

  return (
    <div className="space-y-6 py-4">
      {/* Combined ROAS & Daily Recovery Rate Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recovery Model Curve</CardTitle>
          <p className="text-sm text-slate-500">
            Left: Cumulative ROAS | Right: Daily Recovery Rate
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={450}>
            <ComposedChart data={mergedData}>
              <defs>
                <linearGradient id="roasGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `D${value}`}
              />
              <YAxis
                yAxisId="left"
                stroke="#3b82f6"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                domain={[0, yAxisMaxLeft]}
                tickFormatter={(value) => fmtPct(value)}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                domain={[0, yAxisMaxRight]}
                tickFormatter={(value) => fmtPct(value)}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <div className="space-y-1">
                          <p className="font-semibold">{data.dayLabel}</p>
                          <p className="text-sm text-blue-600">
                            Cumulative ROAS: {fmtPct(data.roas)}
                          </p>
                          <p className="text-sm text-emerald-600">
                            Daily Rate: {fmtPct(data.dailyRate)}
                          </p>
                          <p className="text-sm text-slate-500">
                            Cost: {fmtMoney(data.costsSum)}
                          </p>
                          <p className="text-sm text-slate-500">
                            Revenue: {fmtMoney(data.revenuesSum)}
                          </p>
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Legend />

              {/* Reference lines for key days */}
              {KEY_DAYS.slice(1)
                .filter((day) => day !== 90)
                .map((day) => (
                  <ReferenceLine
                    key={`day-${day}`}
                    yAxisId="left"
                    x={day}
                    stroke="#cbd5e1"
                    strokeDasharray="3 3"
                  />
                ))}

              {/* D90 special reference line */}
              <ReferenceLine
                yAxisId="left"
                x={90}
                stroke="#8b5cf6"
                strokeWidth={2}
                strokeDasharray="5 5"
                label={
                  <CustomLabel
                    value="D90 Our Goal"
                    fill="#8b5cf6"
                    position="insideTopRight"
                  />
                }
              />

              {/* 100% breakeven reference line */}
              <ReferenceLine
                yAxisId="left"
                y={100}
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
                label={
                  <CustomLabel
                    value="100% Breakeven"
                    fill="#ef4444"
                    position="insideRight"
                  />
                }
              />

              <Area
                yAxisId="left"
                type="monotone"
                dataKey="roas"
                stroke="#3b82f6"
                fill="url(#roasGradient)"
                strokeWidth={2}
                legendType="none"
              />

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="roas"
                name="Cumulative ROAS"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={(props) => {
                  const { cx, cy, payload } = props
                  if (KEY_DAYS.includes(payload.day)) {
                    return (
                      <circle
                        key={`dot-${payload.day}`}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="#3b82f6"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )
                  }
                  return null
                }}
              />

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="dailyRate"
                name="Daily Rate"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* All Days Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Summary</CardTitle>
          {roasData[0] && (
            <p className="text-sm text-slate-500">
              Install Date: {roasData[0].startDate} ~ {roasData[0].endDate} |
              Days Count: {roasData[0].dayCount}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>Day</TableHead>
                  <TableHead className="text-right">ROAS</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roasData.map((item) => (
                  <TableRow
                    key={item.day}
                    className={
                      item.day === 90
                        ? 'bg-purple-100'
                        : KEY_DAYS.includes(item.day)
                        ? 'bg-slate-50'
                        : ''
                    }
                  >
                    <TableCell
                      className={`font-medium ${
                        item.day === 90
                          ? 'font-bold text-purple-700'
                          : KEY_DAYS.includes(item.day)
                          ? 'font-bold'
                          : ''
                      }`}
                    >
                      {item.dayLabel}
                      {item.day === 90 && (
                        <span className="ml-2 text-xs text-purple-500">
                          ★ Our Goal
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right font-semibold ${
                        item.roas >= 100
                          ? 'text-green-600'
                          : item.roas >= 90
                          ? 'text-amber-600'
                          : 'text-slate-600'
                      }`}
                    >
                      {fmtPct(item.roas)}
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtMoney(item.costsSum)}
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtMoney(item.revenuesSum)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ModelCurve
